// =============================================================================
// V2MessageBubble.tsx — V2 chat message bubble component
//
// Renders individual messages with sender-dependent alignment and styling.
// User messages: right-aligned, primary background.
// AI messages: left-aligned, muted background, with optional photo thumbnails,
// question buttons, and action buttons.
// =============================================================================

import React from 'react';
import type {
  ChatMessage,
  AttachmentItem,
} from '../../../application/services/notesToHank/NotesToHankTypes';
import { V2QuestionButtons } from './V2QuestionButtons';
import { V2ActionButtons } from './V2ActionButtons';

// ---------------------------------------------------------------------------
// Relative Timestamp Utility
// ---------------------------------------------------------------------------

export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface V2MessageBubbleProps {
  message: ChatMessage;
  isUser: boolean;
  onAnswerSelect?: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText: string,
    questionId?: string
  ) => void;
  onBatchSubmit?: (
    answerIds: string[],
    combinedText: string,
    selectedAnswersMap: Record<string, string>
  ) => void;
  onActionPress?: (
    actionId: string,
    label: string,
    clientCommand?: string
  ) => void;
  pressedActionButtonIds?: Set<string>;
  questionAttachments?: Record<string, AttachmentItem[]>;
  onRemoveQuestionAttachment?: (questionId: string, index: number) => void;
  onRetryUpload?: (questionId: string, attachmentIndex: number) => void;
  isUploading?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function V2MessageBubble({
  message,
  isUser,
  onAnswerSelect,
  onBatchSubmit,
  onActionPress,
  pressedActionButtonIds = new Set(),
  questionAttachments = {},
  onRemoveQuestionAttachment,
  onRetryUpload,
  isUploading = false,
}: V2MessageBubbleProps) {
  const bubbleClasses = isUser
    ? 'ml-auto max-w-[80%] rounded-xl p-3 bg-primary text-primary-foreground'
    : 'mr-auto max-w-[80%] rounded-xl p-3 bg-muted text-foreground';

  const hasPhotos = !isUser && message.photos && message.photos.length > 0;
  const hasQuestions =
    !isUser && message.questions && message.questions.length > 0;
  const hasActionButtons =
    !isUser && message.actionButtons && message.actionButtons.length > 0;

  return (
    <div className={bubbleClasses}>
      {/* Message text */}
      {message.text && (
        <p className="text-sm break-words whitespace-pre-wrap">
          {message.text}
        </p>
      )}

      {/* Photo thumbnails — AI messages only */}
      {hasPhotos && (
        <div className="flex flex-wrap gap-2 mt-2">
          {message.photos!.map((photo, idx) => (
            <img
              key={`photo-${idx}`}
              src={photo.uri}
              alt={`Photo ${idx + 1}`}
              className="max-w-[120px] h-auto rounded-md object-cover"
            />
          ))}
        </div>
      )}

      {/* Question buttons — AI messages only */}
      {hasQuestions && onAnswerSelect && (
        <V2QuestionButtons
          questions={message.questions!}
          onAnswerSelect={onAnswerSelect}
          onBatchSubmit={onBatchSubmit}
          selectedAnswers={message.selectedAnswers || {}}
          questionAttachments={questionAttachments}
          onRemoveQuestionAttachment={onRemoveQuestionAttachment}
          onRetryUpload={onRetryUpload}
          isUploading={isUploading}
        />
      )}

      {/* Action buttons — AI messages only */}
      {hasActionButtons && onActionPress && (
        <V2ActionButtons
          actions={message.actionButtons!}
          onPress={onActionPress}
          pressedIds={pressedActionButtonIds}
        />
      )}

      {/* Relative timestamp */}
      <p className="text-[10px] text-muted-foreground mt-1.5 select-none">
        {formatRelativeTime(message.timestamp)}
      </p>
    </div>
  );
}

export default V2MessageBubble;
